import DiscountBlock from "@components/DiscountBlock"
import { Minus, Pencil } from "@components/icons"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import SelectInput from "@components/inputs/SelectInput"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import {
  body14Styles,
  body16BoldStyles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import { AutomaticDiscountAllocations, CartItem } from "@redux/types"
import { getProductThumbnailAlt } from "@utils/altText"
import { formatContentfulVariant } from "@utils/index"
import {
  DesktopOnly,
  DesktopOnlyContainer,
  MobileAndTabletContainer,
  MobileAndTabletOnly,
} from "@utils/MediaQuery"
import { formatCurrency } from "@utils/numbers"
import { getLocalStorage } from "@utils/storage"
import { getProductURL } from "@utils/urls"
import { Link } from "gatsby"
import _ from "lodash-es"
import React from "react"
import styled, { css } from "styled-components"

const Container = styled.div``

const ItemContainer = styled.div`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  display: flex;
  padding: 24px 16px;

  @media ${breakpoints.tablet} {
    padding: 24px 28px;
  }

  ${MobileAndTabletContainer}, ${DesktopOnlyContainer} {
    flex-grow: 1;
  }
`

const DiscountBlockContainer = styled.div`
  margin-bottom: 8px;
  padding: 16px;
`

const Content = styled.div`
  position: relative;
`

const ImageLink = styled(Link)<any>`
  ${({ to }) => (!to.length ? "pointer-events: none" : "cursor: pointer")};
  flex-grow: 0;
  align-self: flex-start;

  ${ImageHelperContainer} {
    position: relative;
    overflow: hidden;
    width: 80px;
    height: 80px;
    margin-right: 16px;

    @media ${breakpoints.laptop} {
      width: 124px;
      height: 124px;
      margin-right: 26px;
    }
  }
`

const Details = styled.div`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`

const Name = styled.div`
  ${body16BoldStyles}
  padding-right: 48px;
`

const Option = styled.div`
  ${body14Styles}
`

const Label = styled(Option)`
  display: inline;
  font-weight: 700;
  margin-right: 8px;
`

const PriceWrapper = styled.div`
  justify-content: flex-start;
  display: flex;

  @media ${breakpoints.laptop} {
    align-items: center;
  }
`

const RetailPrice = styled.div`
  ${smallcapsRegular13Styles}
  opacity: 0.3;
  margin-right: 6px;
  text-decoration: line-through;
`

const Price = styled.div`
  ${smallcapsRegular13Styles}

  @media ${breakpoints.laptop} {
    color: ${colors.mediumGray};
  }
`

const DiscountedPrice = styled.div`
  ${smallcapsRegular13Styles}
  color: ${colors.brandOrange};

  @media ${breakpoints.laptop} {
    color: ${colors.brandOrange};
  }
`

const buttonBaseProps = css`
  outline: none;
  border: none;
  background: none;

  &:focus,
  &:active {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: pointer;
    }
  }
`

const EditButton = styled.button`
  ${buttonBaseProps}
  position: absolute;
  top: -14px;
  right: -14px;
  flex-direction: row;
  align-items: center;
  display: flex;
  width: 48px;
  height: 48px;
  padding: 14px;

  @media ${breakpoints.tablet} {
    right: 14px;
  }
`

const RemoveButton = styled.button`
  ${buttonBaseProps}
  position: absolute;
  top: -8px;
  right: -16px;
  align-items: center;
  justify-content: center;
  display: flex;
  transition: border ${animation.quick} ${cubicBezier};
  width: 32px;
  height: 32px;
  border: solid 1px ${colors.dividerLineGray};
  border-radius: 50%;

  svg {
    transition:
      color ${animation.quick} ${cubicBezier},
      stroke ${animation.quick} ${cubicBezier};
    stroke: ${colors.mediumDarkGray};
  }

  &:hover {
    border: solid 1px ${colors.brandHoverOutline};

    svg {
      stroke: ${colors.brandHover};
    }
  }
`

const SelectInputWrapper = styled.div`
  width: auto;
  margin-right: 26px;
`

const VerticalSpace = styled.div<{ $height?: string; $laptopHeight?: string }>`
  ${({ $height, $laptopHeight }) => css`
    height: ${$height || "20px"};

    @media ${breakpoints.laptop} {
      height: ${$laptopHeight || $height || "20px"};
    }
  `}
`

const quantityOptions = [...Array(12)].map((_, i) => ({
  label: i + 1,
  value: i + 1,
}))

export interface ItemProps {
  addDiscountedProductToCart: any
  automaticDiscountAllocations: AutomaticDiscountAllocations
  availableSkus: any
  discountBlockProps: Array<{
    discountBlockCallout?: string
    discountBlockTitle?: string
    discountImage?: any
    discountType?: "X" | "Y"
    showDiscountBlock: boolean
  }>
  discountProducts: Array<any>
  item: CartItem
  lineIndex: number
  onClickEdit: (e) => void
  onRemoveCartItem: (item: CartItem, lineIndex: number) => void
  onUpdateCartItem: ({
    cartItem,
    lineIndex,
  }: {
    cartItem: CartItem
    lineIndex: number
  }) => void
  openProductSelector: any
}

const Item: React.FC<ItemProps> = ({
  addDiscountedProductToCart,
  automaticDiscountAllocations,
  availableSkus,
  discountBlockProps,
  discountProducts,
  item,
  lineIndex,
  onClickEdit,
  onRemoveCartItem,
  onUpdateCartItem,
  openProductSelector,
}) => {
  const handleUpdateCartItem = (event) => {
    if (event.target.value) {
      const delta = parseInt(event.target.value) - item.quantity
      const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
      const list = selectedProducts[item.shopifyProductId] || ""
      const elevarProduct = {
        brand: "Urban Armor Gear",
        device: item.device,
        deviceType: item.deviceType,
        image: item.image,
        list,
        price: item.price,
        productId: item.shopifyProductId,
        productName: item.productName,
        quantity: Math.abs(delta),
        sku: item.sku,
        variantId: item.shopifyVariantId,
        variantName: item.variantName,
      }

      if (delta > 0) {
        elevarOnAddToCart(elevarProduct, list)
      } else {
        elevarOnRemoveFromCart(elevarProduct, list)
      }

      onUpdateCartItem({
        cartItem: { ...item, quantity: parseInt(event.target.value) },
        lineIndex,
      })
    }
  }

  const handleUpdateCartItemDebounced = _.debounce(handleUpdateCartItem, 250, {
    leading: true,
    trailing: true,
  })

  const handleOnRemoveCartItem = () => {
    onRemoveCartItem(item, lineIndex)
  }

  const discountAllocation = automaticDiscountAllocations[item.sku]
  const quantityAdjustedDiscountAllocation =
    item.quantity > 1
      ? parseFloat(discountAllocation) / item.quantity
      : parseFloat(discountAllocation)
  const discountedPrice =
    discountAllocation &&
    parseFloat(item.price) - quantityAdjustedDiscountAllocation
  const showDiscountedPrice = Boolean(
    discountedPrice && discountedPrice !== parseFloat(item.price)
  )
  const showCompareAtPrice = Boolean(
    item.compareAtPrice &&
      parseFloat(item.compareAtPrice) !== parseFloat(item.price)
  )

  const { onAddProductToCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart, elevarOnRemoveFromCart } = useElevarEvents()

  const handleOnDiscountClick = (i) => {
    if (
      discountProducts[i].length === 1 &&
      discountProducts[i][0].variants.length === 1
    ) {
      const p = discountProducts[i][0]
      const v = formatContentfulVariant(
        discountProducts[i][0].variants[0]
      ) as any
      const imageSrc = v.images[0] || ""
      const [cleanedSrc] = imageSrc.split("?")

      addDiscountedProductToCart({
        cartProduct: {
          brand: p.brand?.name,
          builtInMagnet: !!p.builtIntMagnet,
          color: v.color,
          compareAtPrice: v.compareAtPrice,
          contentfulProductId: p.contentful_id,
          contentfulVariantId: v.contentfulVariantId,
          device: p.device?.name,
          deviceType: p.device?.type?.name,
          image: `https:${cleanedSrc}`,
          language: p.language,
          marketPrices: v.marketPrices,
          price: v.price,
          productName: p.productName,
          productSlug: p.slug,
          productSubType: p.productSubType?.slug,
          quantity: 1,
          shopifyProductId: p.shopifyProduct?.shopifyProductId,
          shopifyVariantId: v.variantId,
          size: v.size,
          sku: v.sku,
          variantName: v.name,
        },
        showPromoToast: true,
      })

      const elevarProduct: ElevarProductProps = {
        brand: "Urban Armor Gear",
        device: p.device?.name,
        deviceType: p.device?.type?.name,
        image: `https:${cleanedSrc}`,
        list: "upsell",
        price: v.price,
        productId: p.shopifyProduct?.shopifyProductId,
        productName: p.productName,
        quantity: "1",
        sku: v.sku,
        variantId: v.variantId,
        variantName: v.name,
      }

      elevarOnAddToCart(elevarProduct, "upsell")

      const gtmProduct: GTMProductProps = {
        brand: p.brand.name,
        device: p.device.name,
        deviceType: p.device?.type?.name,
        price: v.price,
        productId: p.shopifyProduct?.shopifyProductId,
        productName: p.productName,
        quantity: 1,
        sku: v.sku,
        variantName: v.name,
      }

      onAddProductToCart(gtmProduct)
    } else {
      const handler = openProductSelector[i]
      if (handler) {
        handler()
      }
    }
  }

  return (
    <Container>
      <ItemContainer>
        <ImageLink to={getProductURL(item.productSlug)}>
          <ImageHelper
            alt={getProductThumbnailAlt(item.productName)}
            src={item.image}
          />
        </ImageLink>
        <MobileAndTabletOnly>
          <Content>
            <Details>
              <Name>{`${item.productName} (${item.quantity})`}</Name>
              <VerticalSpace $height="6px" />
              {item.customPrint && item.variantDevice ? (
                <Option>{`${item.variantDevice}`}</Option>
              ) : (
                <Option>{`${item.device}, ${item.variantName}`}</Option>
              )}
              <VerticalSpace $height="12px" />
              <PriceWrapper>
                {Boolean(
                  item.compareAtPrice &&
                    showCompareAtPrice &&
                    !showDiscountedPrice
                ) && (
                  <RetailPrice>
                    {formatCurrency(
                      parseFloat(item.compareAtPrice!),
                      "USD",
                      "en-US"
                    )}
                  </RetailPrice>
                )}
                {discountedPrice && showDiscountedPrice ? (
                  <>
                    <RetailPrice>
                      {formatCurrency(parseFloat(item.price), "USD", "en-US")}
                    </RetailPrice>
                    <DiscountedPrice>
                      {formatCurrency(discountedPrice, "USD", "en-US")}
                    </DiscountedPrice>
                  </>
                ) : (
                  <Price>
                    {formatCurrency(parseFloat(item.price), "USD", "en-US")}
                  </Price>
                )}
              </PriceWrapper>
            </Details>
            <EditButton onClick={onClickEdit}>
              <Pencil />
            </EditButton>
          </Content>
        </MobileAndTabletOnly>
        <DesktopOnly>
          <Content>
            <Details>
              <Name>{item.productName}</Name>
              <VerticalSpace $laptopHeight="12px" />
              {item.customPrint && item.variantDevice ? (
                <Option>
                  <Label>Device:</Label>
                  {`${item.variantDevice}`}
                </Option>
              ) : (
                <>
                  <Option>
                    <Label>Device:</Label>
                    {`${item.device}`}
                  </Option>
                  <VerticalSpace $laptopHeight="6px" />
                  <Option>
                    <Label>Color:</Label>
                    {`${item.color}`}
                  </Option>
                </>
              )}
              {item.language && (
                <>
                  <VerticalSpace $laptopHeight="6px" />
                  <Option>{`Language: ${item.language}`}</Option>
                </>
              )}
              {item.size && (
                <>
                  <VerticalSpace $laptopHeight="6px" />
                  <Option>{`Size: ${item.size}`}</Option>
                </>
              )}
              <VerticalSpace $laptopHeight="24px" />
              <PriceWrapper>
                <SelectInputWrapper>
                  <SelectInput
                    name={`${item.productName}-Quantity`}
                    onChange={handleUpdateCartItemDebounced}
                    options={quantityOptions}
                    value={item.quantity}
                  />
                </SelectInputWrapper>
                {Boolean(
                  item.compareAtPrice &&
                    showCompareAtPrice &&
                    !showDiscountedPrice
                ) && (
                  <RetailPrice>
                    {formatCurrency(
                      parseFloat(item.compareAtPrice!),
                      "USD",
                      "en-US"
                    )}
                  </RetailPrice>
                )}
                {discountedPrice && showDiscountedPrice ? (
                  <>
                    <RetailPrice>
                      {formatCurrency(parseFloat(item.price), "USD", "en-US")}
                    </RetailPrice>
                    <DiscountedPrice>
                      {formatCurrency(discountedPrice, "USD", "en-US")}
                    </DiscountedPrice>
                  </>
                ) : (
                  <Price>
                    {formatCurrency(parseFloat(item.price), "USD", "en-US")}
                  </Price>
                )}
              </PriceWrapper>
            </Details>
            <RemoveButton onClick={handleOnRemoveCartItem}>
              <Minus strokeColor={colors.mediumGray} />
            </RemoveButton>
          </Content>
        </DesktopOnly>
      </ItemContainer>
      {discountBlockProps.map((discountBlock, i) => {
        if (!discountBlock.showDiscountBlock) {
          return null
        }

        const productImage =
          _.get(discountProducts, `${i}.0.productImage`) ||
          (discountBlock.discountImage?.file?.url &&
            `https:${discountBlock.discountImage.file.url}`) ||
          _.get(discountProducts, `${i}.0.variants.0.images.0.file.url`, "")

        return (
          <DiscountBlockContainer key={i}>
            <DiscountBlock
              buttonText={
                discountProducts[i].length === 1 &&
                discountProducts[i][0].variants?.length === 1
                  ? availableSkus.indexOf(
                      discountProducts[i][0].variants.sku
                    ) !== -1
                    ? "Add to Cart"
                    : "Out of Stock"
                  : "Add"
              }
              deal={discountBlock.discountBlockCallout!}
              image={productImage}
              onClick={() => handleOnDiscountClick(i)}
              productName={discountBlock.discountBlockTitle!}
              slug={
                discountProducts[i].length === 1
                  ? discountProducts[i][0].slug
                  : ""
              }
            />
          </DiscountBlockContainer>
        )
      })}
    </Container>
  )
}

export default Item
